import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Trash2, Eye, EyeOff, ImagePlus, Phone, Mail, CheckCircle2, XCircle } from "lucide-react";
import { API, getToken } from "@/api";
import { CONTENT_DEFAULTS, CONTENT_FIELDS } from "@/content/defaults";
import usePageMeta from "@/hooks/usePageMeta";
import SeoHealthTab from "@/components/SeoHealthTab";

const STATUSES = ["new", "contacted", "scheduled", "done"];
const CATEGORIES = [
  { id: "grab-rails", label: "Grab Rails" },
  { id: "hand-rails", label: "Hand Rails" },
  { id: "rubber-ramps", label: "Rubber Ramps" },
];

const FIELD_CLS =
  "min-h-[44px] w-full rounded-xl border border-line bg-ivory px-3.5 py-2.5 text-sm text-ink transition-colors duration-200 focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/30";

function headers() {
  return { Authorization: `Bearer ${getToken()}` };
}

const STATUS_STYLES = {
  new: "bg-sage text-white",
  contacted: "bg-gold/30 text-ink",
  scheduled: "bg-charcoal text-white",
  done: "bg-line text-ink/60",
};

function StatusFilter({ items, filter, setFilter, testidPrefix }) {
  const counts = STATUSES.reduce(
    (acc, s) => ({ ...acc, [s]: items.filter((i) => i.status === s).length }),
    {}
  );
  const options = [{ id: "all", label: "All", count: items.length }, ...STATUSES.map((s) => ({ id: s, label: s, count: counts[s] }))];
  return (
    <div className="mb-5 flex flex-wrap gap-2" role="group" aria-label="Filter enquiries by status" data-testid={testidPrefix}>
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => setFilter(opt.id)}
          data-testid={`${testidPrefix}-${opt.id}`}
          aria-pressed={filter === opt.id}
          className={`min-h-[40px] rounded-full border px-4 text-xs font-bold uppercase tracking-[0.1em] transition-colors duration-200 ${
            filter === opt.id
              ? "border-charcoal bg-charcoal text-white"
              : "border-line bg-white text-ink/60 hover:border-sage hover:text-charcoal"
          }`}
        >
          {opt.label} <span className="opacity-60">({opt.count})</span>
        </button>
      ))}
    </div>
  );
}

function EnquiriesTab() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    axios
      .get(`${API}/enquiries?source=form`, { headers: headers() })
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Could not load enquiries."));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status }, { headers: headers() });
      setItems((list) => list.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success("Status updated.");
    } catch {
      toast.error("Could not update status.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/enquiries/${id}`, { headers: headers() });
      setItems((list) => list.filter((i) => i.id !== id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Could not delete enquiry.");
    }
  };

  if (items === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-sage" aria-label="Loading enquiries" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p data-testid="enquiries-empty" className="rounded-2xl border border-line bg-white p-8 text-center text-sm text-ink/60">
        No enquiries yet. New quote requests will appear here.
      </p>
    );
  }

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  return (
    <div data-testid="enquiries-wrap">
      <StatusFilter items={items} filter={filter} setFilter={setFilter} testidPrefix="enquiries-filter" />
      {filtered.length === 0 ? (
        <p className="rounded-2xl border border-line bg-white p-8 text-center text-sm text-ink/60">
          No enquiries with this status.
        </p>
      ) : (
        <div className="space-y-4" data-testid="enquiries-list">
          {filtered.map((enq, idx) => (
        <article key={enq.id} data-testid={`enquiry-card-${idx}`} className="rounded-2xl border border-line bg-white p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-display text-lg font-bold text-charcoal">{enq.full_name}</h3>
                <span className="rounded-full bg-sage-light px-3 py-1 text-xs font-bold text-sage-dark">{enq.service}</span>
                <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${STATUS_STYLES[enq.status] || "bg-line text-ink/60"}`}>
                  {enq.status}
                </span>
                {enq.tradify_status === "sent" && (
                  <span
                    data-testid={`enquiry-tradify-badge-${idx}`}
                    title={enq.tradify_sent_at ? `Sent ${new Date(enq.tradify_sent_at).toLocaleString("en-AU")}` : "Sent to Tradify"}
                    className="inline-flex items-center gap-1 rounded-full bg-ember/15 px-3 py-1 text-xs font-bold uppercase tracking-wide text-ember-dark"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                    Sent to Tradify
                  </span>
                )}
                {enq.tradify_status === "failed" && (
                  <span
                    data-testid={`enquiry-tradify-badge-${idx}`}
                    title={enq.tradify_last_error || "Tradify forward failed"}
                    className="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-red-600"
                  >
                    <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                    Tradify failed
                  </span>
                )}
                {enq.suburb && <span className="text-xs font-semibold text-ink/50">{enq.suburb}</span>}
              </div>
              <p className="mt-1 text-xs text-ink/50">{new Date(enq.created_at).toLocaleString("en-AU")}</p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={enq.status}
                onChange={(e) => setStatus(enq.id, e.target.value)}
                data-testid={`enquiry-status-${idx}`}
                aria-label={`Status for enquiry from ${enq.full_name}`}
                className="min-h-[40px] rounded-full border border-line bg-ivory px-3 text-xs font-bold text-ink"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.toUpperCase()}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => remove(enq.id)}
                data-testid={`enquiry-delete-${idx}`}
                aria-label={`Delete enquiry from ${enq.full_name}`}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-ink/50 transition-colors duration-200 hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
            <a href={`tel:${enq.phone}`} className="inline-flex items-center gap-2 font-bold text-charcoal">
              <Phone className="h-4 w-4 text-sage-dark" aria-hidden="true" />
              {enq.phone}
            </a>
            {enq.email && (
              <a href={`mailto:${enq.email}`} className="inline-flex items-center gap-2 font-semibold text-ink/70">
                <Mail className="h-4 w-4 text-sage-dark" aria-hidden="true" />
                {enq.email}
              </a>
            )}
            <p className="text-ink/60">
              Preferred contact: <span className="font-semibold text-ink">{enq.preferred_contact}</span>
            </p>
          </div>

          {enq.message && <p className="mt-3 rounded-xl bg-ivory p-4 text-sm leading-relaxed text-ink/80">{enq.message}</p>}

          {enq.photo_path && (
            <a
              href={`${API}/files/${enq.photo_path}?auth=${encodeURIComponent(getToken())}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`enquiry-photo-${idx}`}
              className="mt-4 inline-block"
            >
              <img
                src={`${API}/files/${enq.photo_path}?auth=${encodeURIComponent(getToken())}`}
                alt={`Photo uploaded by ${enq.full_name}`}
                className="h-24 w-24 rounded-xl border border-line object-cover"
                loading="lazy"
              />
            </a>
          )}
        </article>
          ))}
        </div>
      )}
    </div>
  );
}

function GalleryTab() {
  const [items, setItems] = useState(null);
  const [upload, setUpload] = useState({ file: null, category: "grab-rails", alt: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios
      .get(`${API}/gallery/all`, { headers: headers() })
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Could not load gallery."));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const doUpload = async (e) => {
    e.preventDefault();
    if (!upload.file) {
      toast.error("Choose an image first.");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("image", upload.file);
      fd.append("category", upload.category);
      fd.append("alt", upload.alt);
      await axios.post(`${API}/gallery`, fd, { headers: headers() });
      toast.success("Image added to gallery.");
      setUpload({ file: null, category: "grab-rails", alt: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setSaving(false);
    }
  };

  const saveItem = async (item) => {
    try {
      await axios.patch(
        `${API}/gallery/${item.id}`,
        { alt: item.alt, category: item.category, hidden: item.hidden },
        { headers: headers() }
      );
      toast.success("Image updated.");
    } catch {
      toast.error("Could not update image.");
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Remove this image from the gallery?")) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, { headers: headers() });
      setItems((list) => list.filter((i) => i.id !== id));
      toast.success("Image removed.");
    } catch {
      toast.error("Could not remove image.");
    }
  };

  const patch = (id, key, value) =>
    setItems((list) => list.map((i) => (i.id === id ? { ...i, [key]: value } : i)));

  return (
    <div>
      <form onSubmit={doUpload} data-testid="gallery-upload-form" className="rounded-2xl border border-line bg-white p-5 sm:p-6">
        <h3 className="font-display text-lg font-bold text-charcoal">Add a photo</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <label className="flex min-h-[52px] cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-line bg-ivory px-4 text-sm font-semibold text-ink/60 transition-colors duration-200 hover:border-sage hover:text-ink">
            <ImagePlus className="h-4 w-4 text-sage" aria-hidden="true" />
            {upload.file ? upload.file.name : "Choose image"}
            <input
              type="file"
              accept="image/*"
              className="sr-only"
              data-testid="gallery-upload-file"
              onChange={(e) => setUpload((u) => ({ ...u, file: e.target.files?.[0] || null }))}
            />
          </label>
          <select
            value={upload.category}
            onChange={(e) => setUpload((u) => ({ ...u, category: e.target.value }))}
            data-testid="gallery-upload-category"
            aria-label="Image category"
            className={FIELD_CLS}
          >
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
          <input
            value={upload.alt}
            onChange={(e) => setUpload((u) => ({ ...u, alt: e.target.value }))}
            placeholder="Describe the photo (alt text)"
            data-testid="gallery-upload-alt"
            aria-label="Image description"
            className={FIELD_CLS}
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="gallery-upload-submit"
          className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-charcoal px-6 py-2.5 text-xs font-bold tracking-[0.1em] text-white transition-colors duration-300 hover:bg-ink disabled:opacity-60"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          ADD TO GALLERY
        </button>
      </form>

      {items === null ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-sage" aria-label="Loading gallery" />
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-admin-list">
          {items.map((item, idx) => (
            <div key={item.id} data-testid={`gallery-admin-item-${idx}`} className="rounded-2xl border border-line bg-white p-4">
              <img src={item.src} alt={item.alt} loading="lazy" className="aspect-[4/3] w-full rounded-xl object-cover" />
              <div className="mt-3 space-y-3">
                <input
                  value={item.alt}
                  onChange={(e) => patch(item.id, "alt", e.target.value)}
                  aria-label="Image description"
                  data-testid={`gallery-admin-alt-${idx}`}
                  className={FIELD_CLS}
                />
                <div className="flex items-center gap-2">
                  <select
                    value={item.category}
                    onChange={(e) => patch(item.id, "category", e.target.value)}
                    aria-label="Image category"
                    data-testid={`gallery-admin-category-${idx}`}
                    className={`${FIELD_CLS} flex-1`}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      const next = !item.hidden;
                      patch(item.id, "hidden", next);
                      axios
                        .patch(`${API}/gallery/${item.id}`, { hidden: next }, { headers: headers() })
                        .then(() => toast.success(next ? "Image hidden." : "Image visible."))
                        .catch(() => toast.error("Could not update image."));
                    }}
                    aria-label={item.hidden ? "Show image" : "Hide image"}
                    data-testid={`gallery-admin-hide-${idx}`}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line text-ink/60 transition-colors duration-200 hover:bg-sand"
                  >
                    {item.hidden ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    aria-label="Remove image"
                    data-testid={`gallery-admin-delete-${idx}`}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line text-ink/50 transition-colors duration-200 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => saveItem(item)}
                  data-testid={`gallery-admin-save-${idx}`}
                  className="min-h-[40px] w-full rounded-full border-2 border-charcoal text-xs font-bold tracking-[0.1em] text-charcoal transition-colors duration-300 hover:bg-charcoal hover:text-white"
                >
                  SAVE CHANGES
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContentTab() {
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/content`)
      .then((r) => setValues({ ...CONTENT_DEFAULTS, ...(r.data?.overrides || {}) }))
      .catch(() => {
        setValues({ ...CONTENT_DEFAULTS });
        toast.error("Could not load saved content — showing defaults.");
      });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, { overrides: values }, { headers: headers() });
      toast.success("Website content updated.");
    } catch {
      toast.error("Could not save content.");
    } finally {
      setSaving(false);
    }
  };

  if (values === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-sage" aria-label="Loading content" />
      </div>
    );
  }

  return (
    <div data-testid="content-editor">
      {CONTENT_FIELDS.map((group) => (
        <section key={group.group} className="mb-6 rounded-2xl border border-line bg-white p-5 sm:p-6">
          <h3 className="font-display text-lg font-bold text-charcoal">{group.group}</h3>
          <p className="mt-1 text-xs text-ink/50">{group.hint}</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {group.fields.map((field) => (
              <div key={field.key} className={field.multiline ? "sm:col-span-2" : ""}>
                <label htmlFor={`content-${field.key}`} className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-ink/60">
                  {field.label}
                </label>
                {field.multiline ? (
                  <textarea
                    id={`content-${field.key}`}
                    data-testid={`content-${field.key}`}
                    rows={3}
                    value={values[field.key] ?? ""}
                    onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                    className={`${FIELD_CLS} resize-y`}
                  />
                ) : (
                  <input
                    id={`content-${field.key}`}
                    data-testid={`content-${field.key}`}
                    value={values[field.key] ?? ""}
                    onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                    className={FIELD_CLS}
                  />
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
      <button
        type="button"
        onClick={save}
        disabled={saving}
        data-testid="content-save-button"
        className="inline-flex min-h-[52px] items-center gap-2 rounded-full bg-charcoal px-8 py-3.5 text-sm font-bold tracking-[0.08em] text-white transition-colors duration-300 hover:bg-ink disabled:opacity-60"
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {saving ? "SAVING..." : "SAVE ALL CONTENT"}
      </button>
      <p className="mt-3 text-xs text-ink/50">Changes appear on the website as soon as the page is refreshed.</p>
    </div>
  );
}

function LaraEnquiriesTab() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [transcripts, setTranscripts] = useState({});

  const load = useCallback(() => {
    axios
      .get(`${API}/enquiries?source=lara`, { headers: headers() })
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Could not load Lara's enquiries."));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status }, { headers: headers() });
      setItems((list) => list.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success("Status updated.");
    } catch {
      toast.error("Could not update status.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/enquiries/${id}`, { headers: headers() });
      setItems((list) => list.filter((i) => i.id !== id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Could not delete enquiry.");
    }
  };

  const toggleChat = async (sessionId) => {
    if (openId === sessionId) {
      setOpenId(null);
      return;
    }
    setOpenId(sessionId);
    if (!transcripts[sessionId]) {
      try {
        const r = await axios.get(`${API}/chats/${sessionId}`, { headers: headers() });
        setTranscripts((t) => ({ ...t, [sessionId]: r.data.messages || [] }));
      } catch {
        toast.error("Could not load chat transcript.");
      }
    }
  };

  if (items === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-sage" aria-label="Loading Lara's enquiries" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p data-testid="lara-enquiries-empty" className="rounded-2xl border border-line bg-white p-8 text-center text-sm text-ink/60">
        No leads from Lara yet. When visitors share their name and phone number in chat, they'll appear here with their
        wall type, property situation and the full conversation.
      </p>
    );
  }

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  return (
    <div>
      <StatusFilter items={items} filter={filter} setFilter={setFilter} testidPrefix="lara-filter" />
      {filtered.length === 0 ? (
        <p className="rounded-2xl border border-line bg-white p-8 text-center text-sm text-ink/60">
          No leads with this status.
        </p>
      ) : (
    <div className="space-y-4" data-testid="lara-enquiries-list">
      {filtered.map((enq, idx) => (
        <article key={enq.id} data-testid={`lara-enquiry-card-${idx}`} className="rounded-2xl border border-line bg-white p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-display text-lg font-bold text-charcoal">{enq.full_name}</h3>
                <span className="rounded-full bg-charcoal px-3 py-1 text-xs font-bold text-white">VIA LARA</span>
                <span className="rounded-full bg-sage-light px-3 py-1 text-xs font-bold text-sage-dark">{enq.service}</span>
                <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${STATUS_STYLES[enq.status] || "bg-line text-ink/60"}`}>
                  {enq.status}
                </span>
                {enq.tradify_status === "sent" && (
                  <span
                    data-testid={`lara-tradify-badge-${idx}`}
                    title={enq.tradify_sent_at ? `Sent ${new Date(enq.tradify_sent_at).toLocaleString("en-AU")}` : "Sent to Tradify"}
                    className="inline-flex items-center gap-1 rounded-full bg-ember/15 px-3 py-1 text-xs font-bold uppercase tracking-wide text-ember-dark"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                    Sent to Tradify
                  </span>
                )}
                {enq.tradify_status === "failed" && (
                  <span
                    data-testid={`lara-tradify-badge-${idx}`}
                    title={enq.tradify_last_error || "Tradify forward failed"}
                    className="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-red-600"
                  >
                    <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                    Tradify failed
                  </span>
                )}
                {enq.suburb && <span className="text-xs font-semibold text-ink/50">{enq.suburb}</span>}
              </div>
              <p className="mt-1 text-xs text-ink/50">{new Date(enq.created_at).toLocaleString("en-AU")}</p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={enq.status}
                onChange={(e) => setStatus(enq.id, e.target.value)}
                data-testid={`lara-enquiry-status-${idx}`}
                aria-label={`Status for enquiry from ${enq.full_name}`}
                className="min-h-[40px] rounded-full border border-line bg-ivory px-3 text-xs font-bold text-ink"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.toUpperCase()}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => remove(enq.id)}
                data-testid={`lara-enquiry-delete-${idx}`}
                aria-label={`Delete enquiry from ${enq.full_name}`}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-ink/50 transition-colors duration-200 hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
            <a href={`tel:${enq.phone}`} className="inline-flex items-center gap-2 font-bold text-charcoal">
              <Phone className="h-4 w-4 text-sage-dark" aria-hidden="true" />
              {enq.phone}
            </a>
            <p className="text-ink/70">
              Walls: <span className="font-bold text-charcoal">{enq.wall_type || "—"}</span>
            </p>
            <p className="text-ink/70">
              Property: <span className="font-bold text-charcoal">{enq.tenure || "—"}</span>
            </p>
            {enq.address && (
              <p className="text-ink/70">
                Job address: <span className="font-semibold text-charcoal">{enq.address}</span>
              </p>
            )}
          </div>

          {enq.message && <p className="mt-3 rounded-xl bg-ivory p-4 text-sm leading-relaxed text-ink/80">{enq.message}</p>}

          {enq.chat_session_id && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => toggleChat(enq.chat_session_id)}
                data-testid={`lara-enquiry-chat-${idx}`}
                aria-expanded={openId === enq.chat_session_id}
                className="inline-flex min-h-[40px] items-center rounded-full border-2 border-charcoal px-5 text-xs font-bold tracking-[0.1em] text-charcoal transition-colors duration-300 hover:bg-charcoal hover:text-white"
              >
                {openId === enq.chat_session_id ? "HIDE CHAT" : "READ CHAT"}
              </button>
              {openId === enq.chat_session_id && (
                <div className="mt-4 space-y-3 rounded-2xl border border-line bg-ivory p-4">
                  {(transcripts[enq.chat_session_id] || []).map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                          m.role === "user" ? "bg-charcoal text-white" : "border border-line bg-white text-ink"
                        }`}
                      >
                        <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] opacity-60">
                          {m.role === "user" ? "Visitor" : "Lara"}
                        </span>
                        {m.content}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </article>
      ))}
    </div>
      )}
    </div>
  );
}

function ChatsTab() {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/chats`, { headers: headers() })
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Could not load chats."));
  }, []);

  if (items === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-sage" aria-label="Loading chats" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p data-testid="chats-empty" className="rounded-2xl border border-line bg-white p-8 text-center text-sm text-ink/60">
        No chats yet. Conversations with Lara will appear here.
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid="chats-list">
      {items.map((chat, idx) => (
        <article key={chat.session_id} data-testid={`chat-session-${idx}`} className="rounded-2xl border border-line bg-white p-5 sm:p-6">
          <button
            type="button"
            onClick={() => setOpenId(openId === chat.session_id ? null : chat.session_id)}
            data-testid={`chat-session-toggle-${idx}`}
            aria-expanded={openId === chat.session_id}
            className="flex w-full flex-wrap items-center justify-between gap-3 text-left"
          >
            <div>
              <p className="text-sm font-bold text-charcoal">
                {chat.message_count} message{chat.message_count === 1 ? "" : "s"} · {new Date(chat.updated_at).toLocaleString("en-AU")}
              </p>
              <p className="mt-1 line-clamp-1 text-sm text-ink/60">{chat.preview || "—"}</p>
            </div>
            <span className="text-xs font-bold uppercase tracking-[0.12em] text-sage-dark">
              {openId === chat.session_id ? "HIDE" : "READ"}
            </span>
          </button>
          {openId === chat.session_id && (
            <div className="mt-4 space-y-3 border-t border-line pt-4">
              {chat.messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user" ? "bg-charcoal text-white" : "border border-line bg-ivory text-ink"
                    }`}
                  >
                    <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] opacity-60">
                      {m.role === "user" ? "Visitor" : "Lara"}
                    </span>
                    {m.content}
                  </div>
                </div>
              ))}
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

function CampaignResult({ data }) {
  if (!data) return null;
  if (data.raw) {
    return (
      <pre className="mt-6 whitespace-pre-wrap rounded-2xl border border-line bg-white p-6 text-sm leading-relaxed text-ink/80" data-testid="campaign-raw">
        {data.raw}
      </pre>
    );
  }
  return (
    <div className="mt-6 space-y-5" data-testid="campaign-result">
      {(data.campaign_name || data.objective) && (
        <div className="rounded-2xl border border-line bg-charcoal p-6 text-ivory">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sage-tint">Campaign</p>
          <h3 className="mt-2 font-display text-xl font-bold sm:text-2xl">{data.campaign_name}</h3>
          {data.objective && <p className="mt-2 text-sm text-ivory/75">{data.objective}</p>}
        </div>
      )}

      {Array.isArray(data.structure) && (
        <div className="rounded-2xl border border-line bg-white p-6">
          <h4 className="font-display text-lg font-bold text-charcoal">Ad groups & keywords</h4>
          <div className="mt-4 space-y-5">
            {data.structure.map((group, i) => (
              <div key={i} className="rounded-xl bg-ivory p-4" data-testid={`campaign-adgroup-${i}`}>
                <p className="font-display text-base font-bold text-charcoal">{group.name}</p>
                {group.theme && <p className="mt-1 text-xs text-ink/60">{group.theme}</p>}
                <div className="mt-3 flex flex-wrap gap-2">
                  {(group.keywords || []).map((kw, j) => (
                    <span key={j} className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3 py-1.5 text-xs font-semibold text-ink/80">
                      {kw.text}
                      <span className="rounded-full bg-sage-light px-2 py-0.5 text-[10px] font-bold uppercase text-sage-dark">
                        {kw.match_type}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(data.negative_keywords) && (
        <div className="rounded-2xl border border-line bg-white p-6">
          <h4 className="font-display text-lg font-bold text-charcoal">Negative keywords</h4>
          <p className="mt-1 text-xs text-ink/60">Add these so you don't pay for DIY shoppers and job seekers.</p>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="campaign-negatives">
            {data.negative_keywords.map((kw, i) => (
              <span key={i} className="rounded-full bg-sand px-3 py-1.5 text-xs font-semibold text-ink/70">
                {kw}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-5 md:grid-cols-3">
        {data.budget && (
          <div className="rounded-2xl border border-line bg-white p-6" data-testid="campaign-budget">
            <h4 className="font-display text-lg font-bold text-charcoal">Budget</h4>
            <p className="mt-3 font-display text-3xl font-extrabold text-sage-dark">
              ${data.budget.daily_aud}
              <span className="text-sm font-bold text-ink/50">/day</span>
            </p>
            <p className="mt-1 text-sm font-semibold text-ink/70">≈ ${data.budget.monthly_aud}/month</p>
            {data.budget.notes && <p className="mt-3 text-xs leading-relaxed text-ink/60">{data.budget.notes}</p>}
          </div>
        )}
        {data.schedule && (
          <div className="rounded-2xl border border-line bg-white p-6" data-testid="campaign-schedule">
            <h4 className="font-display text-lg font-bold text-charcoal">Schedule</h4>
            <p className="mt-3 text-sm font-bold text-ink">{data.schedule.days}</p>
            <p className="mt-1 text-sm font-semibold text-ink/70">{data.schedule.times}</p>
            {data.schedule.run_length && (
              <p className="mt-2 text-xs font-bold uppercase tracking-wide text-sage-dark">{data.schedule.run_length}</p>
            )}
            {data.schedule.notes && <p className="mt-3 text-xs leading-relaxed text-ink/60">{data.schedule.notes}</p>}
          </div>
        )}
        {data.bidding && (
          <div className="rounded-2xl border border-line bg-white p-6" data-testid="campaign-bidding">
            <h4 className="font-display text-lg font-bold text-charcoal">Bidding</h4>
            <p className="mt-3 text-sm font-bold text-ink">{data.bidding.strategy}</p>
            {data.bidding.notes && <p className="mt-3 text-xs leading-relaxed text-ink/60">{data.bidding.notes}</p>}
          </div>
        )}
      </div>

      {data.ad_copy && (
        <div className="rounded-2xl border border-line bg-white p-6">
          <h4 className="font-display text-lg font-bold text-charcoal">Ad copy</h4>
          <div className="mt-4 grid gap-5 md:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-sage-dark">Headlines</p>
              <ul className="mt-2 space-y-2" data-testid="campaign-headlines">
                {(data.ad_copy.headlines || []).map((h, i) => (
                  <li key={i} className="rounded-lg bg-ivory px-3 py-2 text-sm font-semibold text-ink/80">{h}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-sage-dark">Descriptions</p>
              <ul className="mt-2 space-y-2" data-testid="campaign-descriptions">
                {(data.ad_copy.descriptions || []).map((d, i) => (
                  <li key={i} className="rounded-lg bg-ivory px-3 py-2 text-sm text-ink/80">{d}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-5 md:grid-cols-3">
        {Array.isArray(data.extensions) && (
          <div className="rounded-2xl border border-line bg-white p-6">
            <h4 className="font-display text-lg font-bold text-charcoal">Extensions</h4>
            <ul className="mt-3 space-y-3">
              {data.extensions.map((ext, i) => (
                <li key={i}>
                  <p className="text-sm font-bold text-ink">{ext.type}</p>
                  <p className="text-xs text-ink/60">{(ext.examples || []).join(" · ")}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
        {Array.isArray(data.landing_page_recommendations) && (
          <div className="rounded-2xl border border-line bg-white p-6">
            <h4 className="font-display text-lg font-bold text-charcoal">Landing page tips</h4>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-ink/75">
              {data.landing_page_recommendations.map((tip, i) => (
                <li key={i}>{tip}</li>
              ))}
            </ul>
          </div>
        )}
        {Array.isArray(data.measurement) && (
          <div className="rounded-2xl border border-line bg-white p-6">
            <h4 className="font-display text-lg font-bold text-charcoal">What to track</h4>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-ink/75">
              {data.measurement.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function MarketingTab() {
  const [form, setForm] = useState({
    service: "Grab Rails",
    suburbs: "Perth metro",
    budget: "Recommend a sensible starting budget",
    notes: "",
  });
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [openId, setOpenId] = useState(null);

  const loadHistory = useCallback(() => {
    axios
      .get(`${API}/marketing/campaigns`, { headers: headers() })
      .then((r) => setHistory(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const generate = async () => {
    setGenerating(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/marketing/campaign`, form, { headers: headers(), timeout: 120000 });
      setResult(r.data.result);
      loadHistory();
      toast.success("Campaign plan generated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const copyAll = () => {
    navigator.clipboard.writeText(JSON.stringify(result, null, 2));
    toast.success("Campaign copied to clipboard.");
  };

  return (
    <div>
      <div className="rounded-2xl border border-line bg-white p-6" data-testid="marketing-builder">
        <h3 className="font-display text-lg font-bold text-charcoal">Google Ads Campaign Builder</h3>
        <p className="mt-1 text-sm text-ink/60">
          Your AI marketing helper — builds a complete search campaign with keywords, negative keywords, budget and
          schedule, ready to copy into Google Ads.
        </p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="mkt-service" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-ink/60">
              Service focus
            </label>
            <select
              id="mkt-service"
              data-testid="marketing-service-select"
              value={form.service}
              onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))}
              className={FIELD_CLS}
            >
              <option>Grab Rails</option>
              <option>Hand Rails</option>
              <option>Rubber Ramps</option>
            </select>
          </div>
          <div>
            <label htmlFor="mkt-suburbs" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-ink/60">
              Target areas
            </label>
            <input
              id="mkt-suburbs"
              data-testid="marketing-suburbs-input"
              value={form.suburbs}
              onChange={(e) => setForm((f) => ({ ...f, suburbs: e.target.value }))}
              className={FIELD_CLS}
            />
          </div>
          <div>
            <label htmlFor="mkt-budget" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-ink/60">
              Monthly budget
            </label>
            <select
              id="mkt-budget"
              data-testid="marketing-budget-select"
              value={form.budget}
              onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
              className={FIELD_CLS}
            >
              <option>Recommend a sensible starting budget</option>
              <option>Around $500–$1,000 per month</option>
              <option>Around $1,000–$2,000 per month</option>
              <option>Around $2,000–$4,000 per month</option>
            </select>
          </div>
          <div>
            <label htmlFor="mkt-notes" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-ink/60">
              Notes (optional)
            </label>
            <input
              id="mkt-notes"
              data-testid="marketing-notes-input"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. Focus on bathroom grab rails for seniors"
              className={FIELD_CLS}
            />
          </div>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={generate}
            disabled={generating}
            data-testid="marketing-generate-button"
            className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-charcoal px-7 py-3 text-sm font-bold tracking-[0.08em] text-white transition-colors duration-300 hover:bg-ink disabled:opacity-60"
          >
            {generating && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {generating ? "BUILDING YOUR CAMPAIGN..." : "GENERATE CAMPAIGN"}
          </button>
          {result && (
            <button
              type="button"
              onClick={copyAll}
              data-testid="marketing-copy-button"
              className="inline-flex min-h-[48px] items-center rounded-full border-2 border-charcoal px-6 py-2.5 text-xs font-bold tracking-[0.1em] text-charcoal transition-colors duration-300 hover:bg-charcoal hover:text-white"
            >
              COPY CAMPAIGN
            </button>
          )}
        </div>
        {generating && (
          <p className="mt-3 text-xs text-ink/50">This takes about 20–40 seconds — building keywords, negatives, budget and schedule...</p>
        )}
      </div>

      <CampaignResult data={result} />

      {history.length > 0 && (
        <div className="mt-8">
          <h3 className="font-display text-lg font-bold text-charcoal">Previous campaigns</h3>
          <div className="mt-4 space-y-3" data-testid="marketing-history">
            {history.map((c, i) => (
              <div key={c.id} className="rounded-2xl border border-line bg-white p-5">
                <button
                  type="button"
                  onClick={() => setOpenId(openId === c.id ? null : c.id)}
                  data-testid={`marketing-history-${i}`}
                  aria-expanded={openId === c.id}
                  className="flex w-full flex-wrap items-center justify-between gap-3 text-left"
                >
                  <div>
                    <p className="text-sm font-bold text-charcoal">
                      {c.result?.campaign_name || `${c.service} campaign`}
                    </p>
                    <p className="mt-1 text-xs text-ink/50">
                      {c.service} · {c.suburbs} · {new Date(c.created_at).toLocaleString("en-AU")}
                    </p>
                  </div>
                  <span className="text-xs font-bold uppercase tracking-[0.12em] text-sage-dark">
                    {openId === c.id ? "HIDE" : "VIEW"}
                  </span>
                </button>
                {openId === c.id && <CampaignResult data={c.result} />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { id: "enquiries", label: "Enquiries" },
  { id: "lara", label: "Lara's Enquiries" },
  { id: "gallery", label: "Gallery" },
  { id: "content", label: "Content & SEO" },
  { id: "seo", label: "SEO Health" },
  { id: "chats", label: "Chats" },
  { id: "marketing", label: "Marketing" },
];

export default function Admin() {
  usePageMeta("Modify Admin", "Modify website admin dashboard.");
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("enquiries");

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate("/admin/login", { replace: true });
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: headers() })
      .then(() => setChecking(false))
      .catch(() => {
        localStorage.removeItem("modify_admin_token");
        navigate("/admin/login", { replace: true });
      });
  }, [navigate]);

  if (checking) {
    return (
      <div className="flex justify-center px-4 pb-24 pt-48">
        <Loader2 className="h-6 w-6 animate-spin text-sage" aria-label="Checking sign in" />
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="mx-auto max-w-6xl px-4 pb-24 pt-44 sm:px-6 lg:pt-40">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sage-dark">Modify admin</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-charcoal sm:text-4xl">Dashboard</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/"
            data-testid="admin-view-site-link"
            className="inline-flex min-h-[44px] items-center rounded-full border-2 border-charcoal px-5 py-2 text-xs font-bold tracking-[0.1em] text-charcoal transition-colors duration-300 hover:bg-charcoal hover:text-white"
          >
            VIEW SITE
          </Link>
          <button
            type="button"
            onClick={() => {
              localStorage.removeItem("modify_admin_token");
              navigate("/admin/login", { replace: true });
            }}
            data-testid="admin-logout-button"
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-charcoal px-5 py-2 text-xs font-bold tracking-[0.1em] text-white transition-colors duration-300 hover:bg-ink"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            SIGN OUT
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Admin sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`min-h-[44px] rounded-full border px-5 text-xs font-bold tracking-[0.1em] transition-colors duration-200 ${
              tab === t.id
                ? "border-charcoal bg-charcoal text-white"
                : "border-line bg-white text-ink/70 hover:border-sage hover:text-charcoal"
            }`}
          >
            {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "enquiries" && <EnquiriesTab />}
        {tab === "lara" && <LaraEnquiriesTab />}
        {tab === "gallery" && <GalleryTab />}
        {tab === "content" && <ContentTab />}
        {tab === "seo" && <SeoHealthTab />}
        {tab === "chats" && <ChatsTab />}
        {tab === "marketing" && <MarketingTab />}
      </div>
    </div>
  );
}
